<article class="brandbot-card no-hover flex flex-col h-full rounded-2xl overflow-hidden">
  <div class="px-5 pt-5 pb-3">
    <h3
      class="text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500"
    >
      {{ 'WHERE_ITS_LOST' | translate }}
    </h3>
  </div>

  @if (hasData()) {
    <!-- Headline: how many of the period's opportunities were lost, as a number
         and as a share of the cohort. -->
    <div class="px-5">
      <div class="flex items-end justify-between gap-3">
        <div class="flex flex-wrap items-baseline gap-x-2">
          <span
            class="text-[22px] 2xl:text-[27px] font-semibold tracking-tight tabular-nums leading-none text-[color:var(--text-title)]"
          >
            {{ missedCount() }}
          </span>
          <span class="text-pc-body text-[color:var(--text-subtitle)]">
            {{ 'LOST_MISSED' | translate | lowercase }}
            {{ 'LOST_OF_OPPORTUNITIES' | translate: { total: cohortCount() } }}
          </span>
        </div>
        <!-- The loss rate: the share of the period's opportunities that were lost. -->
        <div class="shrink-0 text-right">
          <span
            class="block text-[22px] 2xl:text-[27px] font-semibold tracking-tight tabular-nums leading-none text-red-700 dark:text-red-400"
          >
            {{ percentOf(missedCount(), cohortCount()) }}%
          </span>
          <span class="mt-1 block text-pc-secondary text-[color:var(--text-subtitle)]">
            {{ 'LOST_RATE' | translate }}
          </span>
        </div>
      </div>
      <div
        class="mt-3 h-[6px] w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800"
        aria-hidden="true"
      >
        <div
          class="h-full rounded-full bg-red-700/85 dark:bg-red-400/80 transition-[width] duration-500"
          [style.width.%]="missedShare()"
        ></div>
      </div>
    </div>

    <!-- The two rows are a PARTITION of the lost count (see the class doc): one
         split bar shows it, then each part with its count and money. -->
    @if (missedCount() > 0) {
      <div class="px-5 mt-4">
        <div class="flex flex-col">
          <div class="flex items-center gap-2.5 py-1.5">
            <span class="size-2 shrink-0 rounded-full bg-red-700/85 dark:bg-red-400/80"></span>
            <span class="flex-1 min-w-0 truncate text-pc-body text-[color:var(--text-title)]">
              {{ 'LOST_AFTER_QUOTING' | translate }}
            </span>
            <span class="text-pc-secondary tabular-nums text-[color:var(--text-subtitle)]">
              {{ afterQuotingCount() }}
              <span class="ml-1 text-pc-secondary opacity-80"
                >· {{ percentOf(afterQuotingCount(), missedCount()) }}%</span
              >
            </span>
            <span
              class="w-32 text-right text-pc-body"
              [class]="
                afterQuotingAmount()
                  ? 'font-medium tabular-nums text-[color:var(--text-title)]'
                  : 'text-[color:var(--text-subtitle)]'
              "
            >
              {{ afterQuotingAmount() ?? ('LOST_NO_AMOUNT' | translate) }}
            </span>
          </div>
          <div class="flex items-center gap-2.5 py-1.5">
            <span class="size-2 shrink-0 rounded-full bg-red-200 dark:bg-red-900/60"></span>
            <span class="flex-1 min-w-0 truncate text-pc-body text-[color:var(--text-title)]">
              {{ 'LOST_BEFORE_QUOTING' | translate }}
            </span>
            <span class="text-pc-secondary tabular-nums text-[color:var(--text-subtitle)]">
              {{ beforeQuotingCount() }}
              <span class="ml-1 text-pc-secondary opacity-80"
                >· {{ percentOf(beforeQuotingCount(), missedCount()) }}%</span
              >
            </span>
            <span class="w-32 text-right text-pc-body text-[color:var(--text-subtitle)]">
              {{ 'LOST_NO_AMOUNT' | translate }}
            </span>
          </div>
        </div>
      </div>
    }

    @if (topReason(); as reason) {
      <div class="px-5 mt-3">
        <div
          class="flex items-center gap-2.5 rounded-xl bg-neutral-50 px-3 py-2.5 dark:bg-neutral-800/50"
        >
          <primecontrol-svg-icon
            class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
            [svgIcon]="'heroicons_outline:tag'"
          ></primecontrol-svg-icon>
          <span
            class="min-w-0 flex-1 text-pc-secondary leading-snug text-[color:var(--text-subtitle)]"
          >
            {{ 'LOST_TOP_REASON_LABEL' | translate }}
            <span class="block truncate text-pc-body font-medium text-[color:var(--text-title)]">
              {{ reason.reason }}
            </span>
          </span>
          <span class="shrink-0 text-right">
            <span
              class="block text-pc-body font-semibold tabular-nums text-[color:var(--text-title)]"
            >
              {{ percentOf(reason.count, missedCount()) }}%
            </span>
            <span class="block text-pc-secondary tabular-nums text-[color:var(--text-subtitle)]">
              {{ 'LOST_REASON_SHARE' | translate: { count: reason.count, total: missedCount() } }}
            </span>
          </span>
        </div>
      </div>
    }

    <!-- Pinned to the foot: a different population (still open, recoverable),
         and the way into the list to act on it. -->
    <a
      [routerLink]="['/customer-support/opportunities']"
      class="group mt-auto flex items-center gap-3 border-t border-neutral-200/70 px-5 py-3.5 transition-colors hover:bg-neutral-50 dark:border-white/10 dark:hover:bg-neutral-800/50"
    >
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)]"
        [svgIcon]="'heroicons_outline:clock'"
      ></primecontrol-svg-icon>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-pc-body text-[color:var(--text-title)]">
          {{ 'LOST_STALLED' | translate }}
        </span>
        <span class="block text-pc-secondary text-[color:var(--text-subtitle)]">
          {{ 'LOST_STALLED_HINT' | translate }}
        </span>
      </span>
      <span class="text-pc-title-sm font-semibold tabular-nums text-[color:var(--text-title)]">
        {{ stalledCount() }}
      </span>
      <primecontrol-svg-icon
        class="icon-size-4 shrink-0 text-[color:var(--text-subtitle)] transition-transform duration-200 group-hover:translate-x-0.5"
        [svgIcon]="'heroicons_solid:chevron-right'"
      ></primecontrol-svg-icon>
    </a>
  } @else if (loading()) {
    <div
      class="pc-skeleton-card relative px-5 pb-5 flex-1 min-h-0 flex items-center justify-center"
      aria-hidden="true"
    >
      <div class="pc-skeleton-aurora"></div>
      <div class="pc-skeleton-ring"></div>
      <div class="relative z-10 w-full h-full flex items-center justify-center">
        <div class="pc-skeleton-tile">
          <div class="pc-skeleton-tile-core"></div>
        </div>
      </div>
    </div>
  } @else if (error()) {
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'DASHBOARD_LOAD_ERROR'"
        [icon]="'heroicons_outline:exclamation-circle'"
      ></primecontrol-empty-data-particules>
    </div>
  } @else {
    <!-- Full-size state, same as the closed-business card above. The icon was
         `hand-thumb-up`, which is not in the registry, so the tile drew empty;
         `thumbs-up` is ("nothing lost" is good news). -->
    <div class="px-5 pb-5 flex-1 min-h-0 flex items-center justify-center">
      <primecontrol-empty-data-particules
        [message]="'NO_LOST_DATA'"
        [icon]="'thumbs-up'"
      ></primecontrol-empty-data-particules>
    </div>
  }
</article>
